import {
  compact,
  every,
  flow,
  forEach,
  get,
  has,
  head,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isPlainObject,
  isString,
  map,
  merge,
  omit,
  reduce,
  set,
} from 'lodash';
import { AbstractType, InjectionToken, Injector, Type } from '@angular/core';

import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import {
  IEvent,
  IInventoryItem,
  IQohInventoryItemVenue,
  NotificationType,
  Primitive,
  REPLENISH_APPLY_ITEMS_SCOPE,
} from '@app/shared/types';
import { IModalDialogResult } from '@app/shared/types/ui/modal-options.type';
import { UiActions } from '@app/store/actions/ui/ui.actions';
import { SECONDS } from '@config/global-constants';
import { rx } from '@app/shared/rx/rx-wrapper';
import { flatTreeDataByHasChildren } from '@app/shared/utils/common-utils';

import {
  bulkReplenishModalBuilder,
  IReplenishConfirmBuilderParams,
  ReplenishConfirmBuilder,
  replenishModalBuilder,
} from './confirm-modal-builders';
import { IReplenishStrategy } from './replenish-strategy/replenish-strategy.types';
import {
  ComplexAdditionDataOpt,
  IReplenishDestination,
  IReplenishEvent,
  IReplenishMapParams,
  IReplenishMessages,
  IReplenishParams,
  IReplenishPercentMap,
  IReplenishWorkerDataMapper,
  PartialReplenishDest,
  ReplenishDest,
} from './replenish.types';
import {
  dispatchCallbackEvent,
  isSkipReplenish,
  takeIntersectionAndProcessThreshold,
} from './replenish.utils';
import {
  appendInitialFactorAndCountValue,
  hasThresholdInItems,
} from '../transfer/transfer.util';
import { ReplenishInventoryDataService } from './replenish-data/replenish-inventory-data.service';
import { ReplenishDataServiceProvider } from './replenish-data/replenish-data-service.provider';
import { XVII18nService } from '@app/i18n/services';

/**
 * Pls not made this injectable and lazy inject or inject by constructor!
 * we should just use this by new instance
 */
class ReplenishWorker<T extends IReplenishDestination = IReplenishDestination> {
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ReplenishDataServiceProvider)
  public replenishDataServiceProvider: ReplenishDataServiceProvider;
  @lazyInject(ReplenishInventoryDataService)
  public inventoryDataService: ReplenishInventoryDataService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  protected confirmBuilder: ReplenishConfirmBuilder;
  protected dataMapper: IReplenishWorkerDataMapper;
  protected additionDataOpts: ComplexAdditionDataOpt;

  protected messages: IReplenishMessages;

  protected on: IReplenishEvent;

  public inject<S>(
    token: Type<S> | InjectionToken<S> | AbstractType<S>,
    notFoundValue?: S,
  ): S {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public constructor(public injector: Injector) {}

  /**
   * shown replenish confirm modal for user
   *
   * @param data modal data
   * @returns user confirm result
   */
  protected showConfirm(
    destination: ReplenishDest,
    modalParams?: IReplenishConfirmBuilderParams,
  ): Promise<IModalDialogResult> {
    if (isEmpty(destination)) {
      return;
    }

    const isBulkReplenish = isArray(destination);

    const defaultConfigBuilder = isBulkReplenish
      ? bulkReplenishModalBuilder
      : replenishModalBuilder;

    const configBuilder = this.confirmBuilder ?? defaultConfigBuilder;

    const modalOptions = configBuilder(
      merge({}, modalParams, {
        replenishSite: destination,
        i18nService: this.i18nService,
      }),
    );

    return this.uiActions.promptFormModal(modalOptions);
  }

  protected async getInventoryItemData(params: {
    percentDesMap?: IReplenishPercentMap;
    event?: IEvent;
    useSimpleFilter?: boolean;
  }): Promise<IInventoryItem[] | null> {
    const fetchItemsDestMap: IReplenishPercentMap = reduce(
      params.percentDesMap,
      (desMap, curDestination, curKey) => {
        return isNil(curDestination.inventory_items) &&
          !get(curDestination, 'replenished')
          ? {
              ...desMap,
              [curKey]: curDestination,
            }
          : desMap;
      },
      {},
    );

    if (isEmpty(fetchItemsDestMap)) {
      return Promise.resolve(null);
    }

    const isAllItemReplenished = every(fetchItemsDestMap, (item) =>
      isEqual(item?.replenished, true),
    );

    if (isAllItemReplenished) {
      return Promise.resolve(null);
    }

    const inventoryItems = await this.inventoryDataService
      .fetch({
        percentDesMap: fetchItemsDestMap,
        event: params.event,
        useSimpleFilter: params.useSimpleFilter,
      })
      .pipe(rx.first())
      .toPromise();

    return inventoryItems?.items;
  }

  protected addItemsDestination(params: {
    percentDesMap?: IReplenishPercentMap;
    replenishApplyItemsScope: REPLENISH_APPLY_ITEMS_SCOPE;
    remoteInventoryItems?: IInventoryItem[];
    [additionInfoKey: string]: Primitive | Object | any;
  }): IReplenishPercentMap {
    const { remoteInventoryItems } = params;

    const inventoryItemsPath = 'inventory_items';

    // merge inventory item into destination
    forEach(params.percentDesMap, (destination) => {
      let inventoryItems: IQohInventoryItemVenue[] = [];

      if (isNil(get(destination, inventoryItemsPath))) {
        inventoryItems = isEmpty(remoteInventoryItems)
          ? []
          : (flow(
              (items) =>
                takeIntersectionAndProcessThreshold(items, destination),
              (items: IQohInventoryItemVenue[]) =>
                appendInitialFactorAndCountValue(items),
            )(remoteInventoryItems) as IQohInventoryItemVenue[]);
      } else {
        inventoryItems = flatTreeDataByHasChildren(
          get(destination, inventoryItemsPath),
        ) as IQohInventoryItemVenue[];
      }

      const someItemsHaveParQty: boolean = hasThresholdInItems(
        inventoryItems,
        undefined,
        isEmpty(params.event),
        params.event,
        params.replenishApplyItemsScope,
      );

      if (!someItemsHaveParQty) {
        destination.noAvailablePAR = true;

        return destination;
      }

      set(destination, inventoryItemsPath, inventoryItems);

      // did this use for replenish not replenish again,Could we improve this to the top?
      if (isEqual(destination.replenished, true)) {
        return destination;
      }

      return destination;
    });

    return params.percentDesMap;
  }

  protected async getAdditionalData(
    params: IReplenishMapParams,
    additionalDataOptPath: 'preInventory' | 'afterInventory',
  ): Promise<{ [additionalKey: string]: any }> {
    const additionDataOpts = get(
      params,
      `additionDataOpts.${additionalDataOptPath}`,
      [],
    );

    const additionDataOptsArr = isArray(additionDataOpts)
      ? additionDataOpts
      : [additionDataOpts];

    if (isEmpty(compact(additionDataOptsArr))) {
      return;
    }

    const additionalDataPromises: Promise<any>[] = map(
      additionDataOptsArr,
      (additionDataOpt) => {
        if (isFunction(additionDataOpt)) {
          return additionDataOpt(omit(params, 'additionDataOpts'));
        }

        const additionalDataService =
          this.replenishDataServiceProvider.getService(additionDataOpt);

        if (!isNil(additionalDataService)) {
          return additionalDataService
            .fetch(params)
            .pipe(
              rx.map((additionDataRes) => ({
                [additionDataOpt as any]: additionDataRes,
              })),
              rx.first(),
            )
            .toPromise();
        }

        return additionDataOpt;
      },
    );

    const additionalDataRes = await Promise.all(additionalDataPromises);

    // summary all additional data
    return reduce(
      additionalDataRes,
      (additionalData, curAdditionalRes) => ({
        ...additionalData,
        ...(isPlainObject(curAdditionalRes)
          ? curAdditionalRes
          : {
              [curAdditionalRes]: curAdditionalRes,
            }),
      }),
      {},
    );
  }

  protected async getDestinations(
    params: IReplenishMapParams,
  ): Promise<[IReplenishPercentMap, IInventoryItem[]]> {
    const remoteInventoryItems = await this.getInventoryItemData({
      ...params,
      event: params.additionalData?.event,
      useSimpleFilter: params.requestOptions?.inventoryItem?.useSimpleFilter,
    });

    const percentDesMap = this.addItemsDestination({
      ...params,
      event: params.additionalData?.event,
      remoteInventoryItems,
    });

    return Promise.resolve([percentDesMap, remoteInventoryItems]);
  }

  protected getFilteredDestMap(
    params: IReplenishMapParams,
  ): IReplenishPercentMap {
    return reduce(
      params.percentDesMap,
      (desMap, curDest, curDestKey) => {
        if (curDest.skipReplenish) {
          return desMap;
        }

        return {
          ...desMap,
          [curDestKey]: curDest,
        };
      },
      <IReplenishPercentMap>{},
    );
  }

  protected async replenishWithStrategy(
    strategy: IReplenishStrategy<T>,
    params: IReplenishMapParams,
  ): Promise<T | T[]> {
    this.uiActions.enableLoader();
    this.uiActions.preventLoaderCloseWhenTimeout(true);

    dispatchCallbackEvent(params.on, 'beforeReplenish', {
      ...omit(params, ['on', 'additionDataOpts', 'strategy']),
    });

    let replenishedDestinations: T | T[];

    try {
      const filteredSkipDestinations = this.getFilteredDestMap(params);

      const preInventoryAdditionalData = await this.getAdditionalData(
        {
          ...params,
          percentDesMap: filteredSkipDestinations,
        },
        'preInventory',
      );

      const [destinationsMap, remoteInventoryItems]: [
        IReplenishPercentMap,
        IInventoryItem[],
      ] = await this.getDestinations({
        ...params,
        additionalData: preInventoryAdditionalData,
        percentDesMap: filteredSkipDestinations,
      });

      const afterInventoryAdditionalData = await this.getAdditionalData(
        {
          ...params,
          percentDesMap: filteredSkipDestinations,
        },
        'afterInventory',
      );

      replenishedDestinations = await strategy.replenish({
        ...omit(params, 'additionDataOpts'),
        percentDesMap: destinationsMap,
        additionalData: {
          ...preInventoryAdditionalData,
          ...afterInventoryAdditionalData,
          remoteInventoryItems,
        },
      });

      const resultIsArr = isArray(replenishedDestinations);

      dispatchCallbackEvent(params.on, 'afterReplenish', {
        destinations:
          resultIsArr && !params.isBulkReplenish
            ? head(replenishedDestinations as T[])
            : replenishedDestinations,
        replenishParams: omit(params, ['on', 'additionDataOpts', 'strategy']),
      });

      if (
        every(
          isArray(replenishedDestinations)
            ? replenishedDestinations
            : [replenishedDestinations],
          (destination) => isSkipReplenish(destination),
        )
      ) {
        let noAvailableMessage = params.messages?.noAvailable;

        if (isFunction(noAvailableMessage)) {
          noAvailableMessage = noAvailableMessage();
        }

        if (!isEmpty(noAvailableMessage)) {
          this.uiActions.showErrorToasterMessage(noAvailableMessage);
        }

        return;
      }

      let successMessage = params.messages?.success;

      if (isFunction(successMessage)) {
        successMessage = successMessage();
      }

      if (!isEmpty(successMessage)) {
        this.uiActions.showToasterMessage(
          isString(successMessage)
            ? {
                type: NotificationType.SUCCESS,
                body: successMessage,
                timeout: 10 * SECONDS,
              }
            : successMessage,
        );
      }
    } catch (error) {
      if (isFunction(params.on?.replenishError)) {
        params.on.replenishError({
          error,
          replenishParams: omit(params, 'on'),
        });
      } else {
        let errorMessage = params.messages?.error;

        if (isFunction(errorMessage)) {
          errorMessage = errorMessage();
        }

        if (!isEmpty(errorMessage)) {
          this.uiActions.showErrorToasterMessage(errorMessage);
        }
      }
    } finally {
      this.uiActions.disableLoader();
    }

    return replenishedDestinations;
  }

  protected mapResult(
    replenishDestination: PartialReplenishDest,
  ): ReplenishDest {
    return isFunction(this.dataMapper?.resultMapper)
      ? this.dataMapper.resultMapper(replenishDestination)
      : (replenishDestination as ReplenishDest);
  }

  public apply<V = any>(
    configPath: string,
    configValue: V,
  ): ReplenishWorker<T> {
    if (!has(this, configPath)) {
      set(this, configPath, configValue);

      return this;
    }

    // eslint-disable-next-line no-console
    console.warn(
      `Can not set config because I not have it. Key is ${configPath}`,
    );

    return this;
  }

  /**
   * apply data mapper for entry data or result data
   *
   * @param mapper mapper object(optional set entry or result or all of it)
   */
  public applyDataMapper(
    mapper: Partial<IReplenishWorkerDataMapper>,
  ): ReplenishWorker<T> {
    const defaultDataMapper: IReplenishWorkerDataMapper = {
      entryMapper: (data) => data as ReplenishDest,
      resultMapper: (data) => data as ReplenishDest,
    };

    this.dataMapper = merge({}, defaultDataMapper, this.dataMapper, mapper);

    return this;
  }

  /**
   * apply replenish confirm options
   *
   * @param options confirm modal options
   */
  public applyConfirmBuilder(
    builder: ReplenishConfirmBuilder,
  ): ReplenishWorker<T> {
    this.confirmBuilder = builder;

    return this;
  }

  public applyAdditionDataOpts(
    dataOpts: ComplexAdditionDataOpt,
  ): ReplenishWorker<T> {
    this.additionDataOpts = dataOpts;

    return this;
  }

  public applyMessages(messages: IReplenishMessages): ReplenishWorker<T> {
    this.messages = messages;

    return this;
  }

  public applyOn(on: IReplenishEvent): ReplenishWorker<T> {
    this.on = on;

    return this;
  }

  /**
   * start to replenish your data
   *
   * @param params
   */
  public async replenish(params: IReplenishParams<T>): Promise<ReplenishDest> {
    const paramsWithInstanceProperty: IReplenishParams<T> = merge(
      <IReplenishParams<T>>{
        on: this.on,
        additionDataOpts: this.additionDataOpts,
        messages: this.messages,
      },
      params,
    );

    const { destination, strategy, on, additionDataOpts, confirmModalOptions } =
      paramsWithInstanceProperty;

    if (isEmpty(destination)) {
      return this.mapResult(destination);
    }

    const convertedData: ReplenishDest = isFunction(
      this.dataMapper?.entryMapper,
    )
      ? this.dataMapper.entryMapper(destination)
      : (destination as ReplenishDest);

    const confirmResult = await this.showConfirm(convertedData, {
      ...confirmModalOptions,
      i18nService: this.i18nService,
    });

    if (!confirmResult.confirmed) return;

    const strategyInstance = new strategy(this.injector);

    const replenishDestinations = await this.replenishWithStrategy(
      strategyInstance,
      {
        on,
        additionDataOpts,
        requestOptions: params.requestOptions,
        percentDesMap: confirmResult.value.items,
        replenishApplyItemsScope: confirmResult.value.itemOption,
        replenishItemsScope: confirmResult.value.replenishOption,
        messages: params.messages,
        isBulkReplenish: isArray(destination),
      },
    );

    return this.mapResult(replenishDestinations);
  }
}

export { ReplenishWorker };
